import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../Icon/Icon';
import './Avatar.scss';

/**
 * Reusable Avatar Component
 * Displays user image or initials with optional verified/online indicators.
 */
export const Avatar = ({
  src,
  name,
  size = 'md',
  isVerified = false,
  isOnline = false,
  isProfile = false,
  hasBorder = true,
  className = '',
  ...props
}) => {
  const getInitials = (name) => {
    if (!name) return 'A';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getIconSize = () => {
    switch (size) {
      case 'sm':
        return 8;
      case 'md':
        return 10;
      case 'lg':
      case 'xl':
        return 12;
      default:
        return 10;
    }
  };

  return (
    <div
      className={`avatar avatar--${size} ${
        isProfile ? 'avatar--profile' : ''
      } ${!hasBorder ? 'avatar--no-border' : ''} ${className}`}
      {...props}
    >
      <div className='avatar__image-container'>
        {src ? (
          <img src={src} alt={name || 'Avatar'} />
        ) : (
          <span className='avatar__initials'>{getInitials(name)}</span>
        )}
      </div>

      {isVerified && (
        <div
          className='avatar__indicator avatar__indicator--verified'
          title='Identity Verified'
        >
          <Icon name='Check' size={getIconSize()} />
        </div>
      )}

      {isOnline && (
        <div
          className='avatar__indicator avatar__indicator--online'
          title='Online'
        />
      )}
    </div>
  );
};

Avatar.propTypes = {
  /** Image URL for the avatar */
  src: PropTypes.string,
  /** User name for fallback initials */
  name: PropTypes.string,
  /** Size of the avatar */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  /** Whether to show verified badge */
  isVerified: PropTypes.bool,
  /** Whether to show online indicator */
  isOnline: PropTypes.bool,
  /** Whether to show profile size style (120px) */
  isProfile: PropTypes.bool,
  /** Whether to show border around the avatar */
  hasBorder: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default Avatar;
